<template>
  <!-- POC Cuidados Paliativos (Tela 6): registro do óbito e trilha de luto.
       Aparece na Ficha quando a Fase da jornada é "Falecido". -->
  <div class="death-record">
    <div
      v-if="record"
      class="dr-summary"
    >
      <ul class="dr-chips">
        <li class="dr-chip">
          {{ placeName(record.deathPlace) }}
        </li>
        <li
          class="dr-chip"
          :class="desiredTone(record.wasDesired)"
        >
          {{ desiredText(record.wasDesired) }}
        </li>
        <li
          v-if="record.griefTrail"
          class="dr-chip is-ok"
        >
          Acompanhamento pós-óbito
        </li>
      </ul>
      <button
        type="button"
        class="dr-link"
        @click="openModal"
      >
        Editar registro
      </button>
    </div>

    <button
      v-else
      type="button"
      class="dr-link"
      @click="openModal"
    >
      Registrar local do óbito e trilha de luto
    </button>

    <!-- Modal -->
    <div class="default-modal">
      <div
        class="modal"
        :class="{ 'is-active': open }"
      >
        <div
          class="modal-background"
          @click="close"
        />
        <div
          class="modal-card dr-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dr-modal-title"
        >
          <header class="modal-card-head">
            <p
              id="dr-modal-title"
              class="modal-card-title"
            >
              Registro do óbito
            </p>
          </header>

          <section class="modal-card-body">
            <div class="dr-block">
              <lila-datepicker
                v-model="form.deathDate"
                label="Data do óbito"
                placeholder="Selecione"
                :disabled-date="isFutureDate"
              />
              <p
                v-if="deathDateError && showErrors"
                class="dr-error"
              >
                {{ deathDateError }}
              </p>
            </div>

            <fieldset class="dr-block">
              <legend class="dr-label">
                Onde aconteceu
              </legend>
              <div class="dr-pills">
                <label
                  v-for="option in deathPlaceOptions"
                  :key="option._id"
                  class="dr-pill"
                  :class="{ 'is-selected': form.deathPlace === option._id }"
                >
                  <input
                    type="radio"
                    class="dr-visually-hidden"
                    :name="`deathPlace-${patientId}`"
                    :value="option._id"
                    :checked="form.deathPlace === option._id"
                    @change="chooseDeathPlace(option._id)"
                  >
                  {{ option.name }}
                </label>
              </div>
              <p
                v-if="!form.deathPlace && showErrors"
                class="dr-error"
              >
                Campo obrigatório.
              </p>
            </fieldset>

            <fieldset class="dr-block">
              <legend class="dr-label">
                Era o local que o paciente desejava?
              </legend>
              <div class="dr-pills">
                <label
                  v-for="option in desiredOptions"
                  :key="option._id"
                  class="dr-pill"
                  :class="{ 'is-selected': form.wasDesired === option._id }"
                >
                  <input
                    v-model="form.wasDesired"
                    type="radio"
                    class="dr-visually-hidden"
                    :name="`wasDesired-${patientId}`"
                    :value="option._id"
                  >
                  {{ option.name }}
                </label>
              </div>
              <p class="dr-hint">
                {{ preferenceHint }}
              </p>
            </fieldset>

            <div class="dr-block dr-grief">
              <label class="dr-check">
                <input
                  v-model="form.griefTrail"
                  type="checkbox"
                  :disabled="trailAlreadyCreated"
                >
                Criar trilha de luto
              </label>
              <p class="dr-hint">
                {{ trailAlreadyCreated
                  ? "Trilha de luto já criada. As ações estão na aba Ações e na Agenda."
                  : "Acompanhamento da família depois do óbito. Cria duas ações, que aparecem na aba Ações e na Agenda:" }}
              </p>
              <ul
                v-if="form.griefTrail && !trailAlreadyCreated"
                class="dr-trail"
              >
                <li
                  v-for="step in griefSteps"
                  :key="step.days"
                >
                  <strong>{{ formatDay(dueDate(step.days)) }}</strong> · {{ step.title }}
                </li>
              </ul>
            </div>
          </section>

          <footer class="modal-card-foot">
            <p
              v-if="saveError"
              class="dr-save-error"
              role="alert"
            >
              {{ saveError }}
            </p>
            <lila-button
              type="button"
              title="Registrar"
              :is-centralized="true"
              :second-button="{ type: 'button', title: 'Cancelar' }"
              @custom-click-event="save"
              @custom-click-cancel-event="close"
            />
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@components/shared/button/Button.vue";
import LilaDatepicker from "@components/shared/fields/LilaDatepicker.vue";
import { eventBus } from "@/eventBus.js";
import {
  CARE_PLAN_UPDATED_EVENT,
  DEATH_PLACE_OPTIONS,
  DESIRED_PLACE_OPTIONS,
  getCarePlan,
  getDeathRecord,
  placeLabel,
  saveDeathRecord
} from "@/services/palliative/carePlanApi";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

export default {
  name: "PalliativeDeathRecord",
  components: {
    "lila-button": Button,
    "lila-datepicker": LilaDatepicker
  },
  // Registro feito por outro profissional atualiza o resumo (com o modal fechado).
  mixins: [pocLiveSync("reloadRecord")],
  props: {
    patientId: {
      type: [String, Number],
      required: true
    },
    // Controla o modal (a Ficha abre ao escolher "Falecido").
    open: {
      type: Boolean,
      default: false
    },
    // Data de falecimento da Ficha, em DD/MM/YYYY.
    deathDate: {
      type: String,
      default: ""
    },
    // Acompanhante da Ficha: usado se o plano de cuidados não tiver cuidadora.
    companionName: {
      type: String,
      default: ""
    }
  },
  emits: ["update:open", "update:deathDate"],
  data() {
    return {
      record: null,
      // Lido do plano de cuidados (Tela 4) sempre que o modal abre.
      carePlan: null,
      form: this.emptyForm(),
      showErrors: false,
      saving: false,
      saveError: "",
      deathPlaceOptions: DEATH_PLACE_OPTIONS,
      desiredOptions: DESIRED_PLACE_OPTIONS
    };
  },
  computed: {
    caregiverName() {
      return this.carePlan?.caregiverName || this.companionName || "";
    },
    trailAlreadyCreated() {
      return Boolean(this.record?.griefActionIds?.length);
    },
    // Trilha de luto: ligação para a cuidadora em 7 dias e psicólogo em 30 dias.
    griefSteps() {
      const who = this.caregiverName ? `Ligar para ${this.caregiverName}` : "Ligar para a cuidadora";
      return [
        { days: 7, title: `${who} (acolhimento no luto)` },
        { days: 30, title: "Acolhimento da família com psicólogo" }
      ];
    },
    // A data do óbito é obrigatória e não pode ser futura: o registro marca o paciente como falecido.
    deathDateError() {
      if (!this.form.deathDate) return "Campo obrigatório.";
      const date = this.$moment(this.form.deathDate, "DD/MM/YYYY", true);
      if (!date.isValid()) return "Data inválida.";
      if (date.isAfter(this.$moment(), "day")) return "A data do óbito não pode ser futura.";
      return "";
    },
    preferenceHint() {
      const wanted = this.carePlan?.deathPlace;
      if (!wanted || wanted === "indefinido") {
        return "O plano de cuidados não tem a preferência de local registrada.";
      }
      return `Pelo plano de cuidados, o paciente preferia estar ${placeLabel(wanted).toLowerCase()}.`;
    }
  },
  watch: {
    patientId: {
      immediate: true,
      handler() {
        this.loadRecord();
      }
    },
    open(isOpen) {
      if (isOpen) this.fillForm();
    }
  },
  mounted() {
    if (this.open) this.fillForm();
  },
  methods: {
    async loadRecord() {
      const patientId = this.patientId;
      try {
        const record = await getDeathRecord(patientId);
        if (String(patientId) === String(this.patientId)) this.record = record;
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o registro de óbito.", error);
      }
    },
    reloadRecord() {
      if (!this.open) this.loadRecord();
    },
    emptyForm() {
      return { deathDate: "", deathPlace: "", wasDesired: "", griefTrail: true };
    },
    async fillForm() {
      this.showErrors = false;
      this.saveError = "";
      this.form = this.record
        ? { ...this.emptyForm(), ...this.record, deathDate: this.deathDate || this.record.deathDate || "" }
        : { ...this.emptyForm(), deathDate: this.deathDate || this.$moment().format("DD/MM/YYYY") };
      try {
        this.carePlan = await getCarePlan(this.patientId);
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o plano de cuidados.", error);
        this.carePlan = null;
      }
    },
    openModal() {
      this.$emit("update:open", true);
    },
    close() {
      this.$emit("update:open", false);
    },
    // Sugere "era o local desejado?" comparando com o plano de cuidados.
    chooseDeathPlace(place) {
      this.form.deathPlace = place;
      const wanted = this.carePlan?.deathPlace;
      if (wanted && wanted !== "indefinido") {
        this.form.wasDesired = wanted === place ? "sim" : "nao";
      }
    },
    isFutureDate(date) {
      return this.$moment(date).isAfter(this.$moment(), "day");
    },
    placeName(id) {
      const option = DEATH_PLACE_OPTIONS.find((o) => o._id === id);
      return option ? `Óbito ${option.name.toLowerCase()}` : "Óbito registrado";
    },
    desiredText(value) {
      if (value === "sim") return "No local desejado";
      if (value === "nao") return "Fora do local desejado";
      return "Local desejado não informado";
    },
    desiredTone(value) {
      if (value === "sim") return "is-ok";
      if (value === "nao") return "is-pending";
      return "";
    },
    baseDate() {
      const date = this.$moment(this.form.deathDate, "DD/MM/YYYY", true);
      return date.isValid() ? date : this.$moment();
    },
    dueDate(days) {
      return this.baseDate().clone().add(days, "days").format("YYYY-MM-DD");
    },
    formatDay(date) {
      return this.$moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
    },
    // A API marca o paciente como falecido (LifeStatus 3 e data do óbito) e,
    // com a trilha de luto, cria as ações de D+7 e D+30 a partir da data do óbito.
    async save() {
      if (!this.form.deathPlace || this.deathDateError) {
        this.showErrors = true;
        return;
      }
      if (this.saving) return;

      this.saving = true;
      this.saveError = "";
      try {
        this.record = await saveDeathRecord(this.patientId, {
          deathDate: this.form.deathDate,
          deathPlace: this.form.deathPlace,
          wasDesired: this.form.wasDesired,
          griefTrail: this.form.griefTrail
        });
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível registrar o óbito.", error);
        this.saveError = error.message;
        return;
      } finally {
        this.saving = false;
      }

      this.$emit("update:deathDate", this.form.deathDate);
      eventBus.emit(CARE_PLAN_UPDATED_EVENT, { patientId: String(this.patientId) });
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-card-foot {
  flex-wrap: wrap;
}

.dr-save-error {
  width: 100%;
  margin: 0 0 8px;
  color: #db466f;
  font-size: 13px;
  text-align: center;
}

$primary: #4f3f82;
$primary-soft: #ece9f5;
$ink: #2b2b3a;
$label: #484848;
$muted: #6b6b7b;
$border: #dcd8e6;

.death-record {
  margin-top: 12px;
}

.dr-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.dr-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.dr-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  background-color: $primary-soft;

  &.is-ok,
  &.is-pending {
    font-weight: 700;

    &::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: currentColor;
    }
  }

  &.is-ok {
    color: #1e6b3a;
    background-color: #e3f4e8;
  }

  &.is-pending {
    color: #8a5a00;
    background-color: #fff3d6;
  }
}

.dr-link {
  padding: 0;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  text-decoration: underline;
  background: none;
  cursor: pointer;
}

.dr-modal {
  max-width: 560px;

  .modal-card-body {
    text-align: left;
  }
}

.dr-block {
  min-width: 0;
  margin: 0 0 20px;
  padding: 0;
  border: 0;

  :deep(.label) {
    margin: 0 0 10px 20px;
    font-size: 16px;
    font-weight: 700;
    color: $label;
  }
}

.dr-label {
  display: block;
  margin: 0 0 10px 20px;
  padding: 0;
  font-size: 16px;
  line-height: 18px;
  font-weight: 700;
  color: $label;
}

.dr-hint {
  margin: 8px 0 0 20px;
  font-size: 12px;
  line-height: 16px;
  color: $muted;
}

.dr-error {
  margin: 8px 0 0 20px;
  font-size: 12px;
  color: #cc0f35;
}

.dr-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.dr-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dr-pill {
  position: relative;
  padding: 8px 16px;
  border: 1px solid $border;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  background-color: #ffffff;
  cursor: pointer;

  &:hover {
    background-color: $primary-soft;
  }

  &.is-selected {
    border-color: $primary;
    color: #ffffff;
    background-color: $primary;
  }

  &:focus-within {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}

.dr-grief {
  margin-bottom: 0;
  padding-top: 16px;
  border-top: 1px solid #f0eef5;
}

.dr-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
  font-size: 16px;
  font-weight: 700;
  color: $label;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: $primary;
  }
}

.dr-trail {
  margin: 10px 0 0 20px;
  padding: 0;
  list-style: none;

  li {
    margin: 0 0 6px;
    font-size: 14px;
    color: $ink;
  }
}
</style>
